import React, { useState } from 'react';
import LegalPage from './LegalPage';
import CookieSettingsModal from '../shared/CookieSettingsModal';

export default function CookiePolicy() {
  const [showSettings, setShowSettings] = useState(false);

  const content = (
    <div>
      <p className="text-gray-600 mb-6">
        <strong>Effective Date:</strong> October 1, 2025
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Introduction</h2>
      <p className="mb-4">
        This Cookie Policy explains how EQ Community INC ("EQ," "we," "us," or "our") uses cookies and 
        similar tracking technologies on the EQ Assistant platform at https://www.eqassistant.com (the "Platform").
      </p>
      <p className="mb-4">
        By using the Platform, you consent to the use of cookies as described in this policy. You can manage 
        your cookie preferences at any time through your browser settings or our cookie consent manager.
      </p>

      <button
        onClick={() => setShowSettings(true)}
        className="mb-6 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors shadow-md"
      >
        Manage Cookie Preferences
      </button>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">What Are Cookies?</h2>
      <p className="mb-4">
        Cookies are small text files stored on your device (computer, tablet, smartphone) when you visit websites. 
        Cookies allow websites to remember your preferences, authenticate your identity, and analyze usage patterns.
      </p>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Types of Cookies:</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Session Cookies:</strong> Temporary cookies deleted when you close your browser</li>
        <li><strong>Persistent Cookies:</strong> Remain on your device for a set period</li>
        <li><strong>First-Party Cookies:</strong> Set by eqassistant.com</li>
        <li><strong>Third-Party Cookies:</strong> Set by trusted EQ.app partners (identity, payments, analytics)</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Cookies We Use</h2>

      <div className="space-y-6">
        {/* Strictly Necessary */}
        <div className="bg-gray-50 rounded-lg p-5 border-2 border-gray-200">
          <div className="flex items-center gap-3 mb-3">
            <span className="bg-gray-300 text-gray-800 px-3 py-1 rounded-full text-xs font-bold uppercase">
              Always Active
            </span>
            <h3 className="text-xl font-bold text-gray-900">Strictly Necessary Cookies</h3>
          </div>
          <p className="mb-3 text-gray-700">
            Essential cookies required for the Platform to function. Cannot be disabled.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-200">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Cookie Name</th>
                  <th className="px-4 py-2 text-left font-semibold">Purpose</th>
                  <th className="px-4 py-2 text-left font-semibold">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-300">
                  <td className="px-4 py-2 font-mono text-xs">auth0</td>
                  <td className="px-4 py-2">Authentication session (EQ.app identity partner)</td>
                  <td className="px-4 py-2">Session</td>
                </tr>
                <tr className="border-t border-gray-300">
                  <td className="px-4 py-2 font-mono text-xs">_stripe_mid</td>
                  <td className="px-4 py-2">Payment security (EQ.app payment partner)</td>
                  <td className="px-4 py-2">1 year</td>
                </tr>
                <tr className="border-t border-gray-300">
                  <td className="px-4 py-2 font-mono text-xs">XSRF-TOKEN</td>
                  <td className="px-4 py-2">Security protection</td>
                  <td className="px-4 py-2">Session</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-gray-600">
            <strong>Legal Basis:</strong> Legitimate Interest (necessary for service provision)
          </p>
        </div>

        {/* Functional */}
        <div className="bg-white rounded-lg p-5 border-2 border-indigo-200">
          <div className="flex items-center gap-3 mb-3">
            <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-xs font-bold uppercase">
              Requires Consent
            </span>
            <h3 className="text-xl font-bold text-gray-900">Functional Cookies</h3>
          </div>
          <p className="mb-3 text-gray-700">
            Enable enhanced functionality and personalization. Can be disabled.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-indigo-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Cookie Name</th>
                  <th className="px-4 py-2 text-left font-semibold">Purpose</th>
                  <th className="px-4 py-2 text-left font-semibold">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-indigo-100">
                  <td className="px-4 py-2 font-mono text-xs">eq_user_preferences</td>
                  <td className="px-4 py-2">User settings and preferences</td>
                  <td className="px-4 py-2">90 days</td>
                </tr>
                <tr className="border-t border-indigo-100">
                  <td className="px-4 py-2 font-mono text-xs">eq_search_history</td>
                  <td className="px-4 py-2">Recent searches for quick access</td>
                  <td className="px-4 py-2">30 days</td>
                </tr>
                <tr className="border-t border-indigo-100">
                  <td className="px-4 py-2 font-mono text-xs">eq_campaign_view</td>
                  <td className="px-4 py-2">Campaign display preferences</td>
                  <td className="px-4 py-2">90 days</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-gray-600">
            <strong>Legal Basis:</strong> Consent (can be disabled via cookie settings)
          </p>
        </div>

        {/* Analytics */}
        <div className="bg-white rounded-lg p-5 border-2 border-blue-200">
          <div className="flex items-center gap-3 mb-3">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold uppercase">
              Requires Consent
            </span>
            <h3 className="text-xl font-bold text-gray-900">Analytics Cookies</h3>
          </div>
          <p className="mb-3 text-gray-700">
            Help us understand how you use the Platform. Can be disabled.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-blue-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">Cookie Name</th>
                  <th className="px-4 py-2 text-left font-semibold">Purpose</th>
                  <th className="px-4 py-2 text-left font-semibold">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-blue-100">
                  <td className="px-4 py-2 font-mono text-xs">eq_analytics_id</td>
                  <td className="px-4 py-2">Internal usage tracking</td>
                  <td className="px-4 py-2">1 year</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-gray-600">
            <strong>Note:</strong> We do not currently use Google Analytics or third-party analytics services.
          </p>
          <p className="mt-1 text-xs text-gray-600">
            <strong>Legal Basis:</strong> Consent (can be disabled via cookie settings)
          </p>
        </div>

        {/* Marketing */}
        <div className="bg-white rounded-lg p-5 border-2 border-purple-200">
          <div className="flex items-center gap-3 mb-3">
            <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-bold uppercase">
              Not Currently Used
            </span>
            <h3 className="text-xl font-bold text-gray-900">Marketing Cookies</h3>
          </div>
          <p className="text-gray-700">
            May be set by advertising partners. We do not currently use marketing cookies. If we implement 
            them in the future, we will update this policy and obtain explicit consent.
          </p>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Local Storage and Similar Technologies</h2>
      <p className="mb-4">
        In addition to cookies, we use browser Local Storage and Session Storage:
      </p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>eq_guest_session:</strong> Guest user session tracking (necessary for search limits)</li>
        <li><strong>eq_cookie_consent:</strong> Your cookie consent preferences</li>
        <li><strong>auth0.ssodata:</strong> EQ.app identity partner single sign-on data (authentication)</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Third-Party Cookies</h2>
      
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-bold text-gray-900 mb-2">EQ.app Identity Partner (Authentication)</h4>
          <p className="text-sm text-gray-700 mb-2">
            Purpose: Secure user authentication and single sign-on
          </p>
          <p className="text-xs text-gray-600">
            Policy: Available from EQ.app upon request
          </p>
          <p className="text-xs text-gray-600">
            Control: Required for authentication; cannot be disabled
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <h4 className="font-bold text-gray-900 mb-2">EQ.app Payment Partner (Payment Processing)</h4>
          <p className="text-sm text-gray-700 mb-2">
            Purpose: Secure payment processing and fraud prevention
          </p>
          <p className="text-xs text-gray-600">
            Policy: Available from EQ.app upon request
          </p>
          <p className="text-xs text-gray-600">
            Control: Required for payments; cannot be disabled if using paid plans
          </p>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">How to Manage Cookies</h2>
      
      <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
        <h3 className="font-bold text-indigo-900 mb-2">Cookie Consent Manager</h3>
        <p className="text-sm text-indigo-800 mb-3">
          You can customize cookie preferences through our cookie consent banner or settings:
        </p>
        <button
          onClick={() => setShowSettings(true)}
          className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors text-sm"
        >
          Manage Cookie Preferences
        </button>
      </div>

      <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Browser Settings:</h3>
      <ul className="list-disc pl-6 mb-4 space-y-2 text-sm">
        <li><strong>Google Chrome:</strong> Settings → Privacy and security → Cookies and other site data</li>
        <li><strong>Firefox:</strong> Options → Privacy & Security → Cookies and Site Data</li>
        <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
        <li><strong>Microsoft Edge:</strong> Settings → Cookies and site permissions</li>
      </ul>

      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
        <p className="text-sm text-yellow-800">
          <strong>Warning:</strong> Blocking strictly necessary cookies will prevent you from using essential 
          Platform features, including authentication and payment processing.
        </p>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Impact of Disabling Cookies</h2>
      
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left font-semibold border-b border-gray-200">Cookie Category</th>
              <th className="px-4 py-3 text-left font-semibold border-b border-gray-200">Impact if Disabled</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200">
              <td className="px-4 py-3 font-medium">Strictly Necessary</td>
              <td className="px-4 py-3 text-red-700">
                Platform will not function; cannot log in or use core features
              </td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-4 py-3 font-medium">Functional</td>
              <td className="px-4 py-3 text-yellow-700">
                Loss of personalization; settings not remembered
              </td>
            </tr>
            <tr className="border-b border-gray-200">
              <td className="px-4 py-3 font-medium">Analytics</td>
              <td className="px-4 py-3 text-green-700">
                No impact on functionality; we cannot analyze usage patterns
              </td>
            </tr>
            <tr>
              <td className="px-4 py-3 font-medium">Marketing</td>
              <td className="px-4 py-3 text-green-700">
                No impact (not currently used)
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Your Rights (GDPR)</h2>
      <p className="mb-4">
        Under GDPR, you have the right to:
      </p>
      <ul className="list-disc pl-6 mb-4 space-y-2">
        <li><strong>Access:</strong> Request information about cookies used to process your data</li>
        <li><strong>Deletion:</strong> Delete cookies through browser settings or our consent manager</li>
        <li><strong>Objection:</strong> Object to non-essential cookies</li>
        <li><strong>Withdrawal:</strong> Withdraw consent for non-essential cookies at any time</li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Updates to This Cookie Policy</h2>
      <p className="mb-4">
        We may update this Cookie Policy periodically. We will notify you of material changes by updating 
        the "Last Updated" date and displaying a notice on the Platform.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">Contact Us</h2>
      <p className="mb-4">
        If you have questions about our use of cookies, please contact:
      </p>
      <div className="bg-gray-100 rounded-lg p-4 mb-4">
        <p className="mb-2"><strong>EQ Community INC</strong></p>
        <p className="mb-1">Email: <a href="mailto:hello@eq.community" className="text-indigo-600 hover:underline">hello@eq.community</a></p>
        <p className="mb-1">Support: <a href="mailto:hello@eq.community" className="text-indigo-600 hover:underline">hello@eq.community</a></p>
        <p>DPO: <a href="mailto:hello@eq.community" className="text-indigo-600 hover:underline">hello@eq.community</a></p>
      </div>

      <div className="mt-8 pt-6 border-t border-gray-200 text-sm text-gray-600">
        <p><strong>Version:</strong> 1.0</p>
        <p><strong>Effective Date:</strong> October 1, 2025</p>
        <p><strong>Last Updated:</strong> October 1, 2025</p>
        <p className="mt-2">Copyright © 2025 EQ Community INC. All rights reserved.</p>
      </div>

      {/* Cookie Settings Modal */}
      <CookieSettingsModal 
        isOpen={showSettings} 
        onClose={() => setShowSettings(false)} 
      />
    </div>
  );

  return (
    <LegalPage
      title="Cookie Policy"
      content={content}
      lastUpdated="October 1, 2025"
    />
  );
}
